import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useMemo } from "react";

import { SimulatorPickActionCell } from "@/components/Reports/SimulatorPickActionCell";
import { DataTable } from "@/components/ui/data-table";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toNaturalMetricDefinition } from "../../../../utils/marketContextMetricDefinitions";
import { pnlBarFill } from "../../Statistics/components/statsSignedPnlBarUtils";
import { TradeLogSortableCell } from "../../TradeLog/components/tradeLogSortableCell";
import { marketContextVariantTooltipText, candleDirectionMetricKeyFromLabelText, ensureSentence } from "./marketContextVariantTooltipText";
import {
  AddToSimulatorColumnHeader,
  MarketContextActionColumnCell,
  MarketContextPreviewTradesButton,
  MarketContextPreviewTradesColumnHeader,
  MarketContextTooltipContent,
  fmtInt,
  fmtNum,
  fmtPct,
  fmtPnl,
  MarketContextMetricColumnHeader,
} from "./marketContextDataTableShared";
import { WIN_RATE_COLUMN_TOOLTIP } from "./marketContextTableTooltips";

export type ConfiguredSliceResultRow = {
  id: string;
  sliceLabel: string;
  sample: number | undefined;
  winRate: number | undefined;
  expectancy: number | undefined;
  totalPnl: number | undefined;
  bestSituation: string | undefined;
  worstSituation: string | undefined;
};

const MARKET_CONTEXT_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

const MARKET_CONTEXT_FIELD_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500";

export type MarketContextConfiguredSliceResultTableProps = {
  title: string;
  description: string;
  row: ConfiguredSliceResultRow;
  metricDefinitions?: Record<string, unknown>;
  onAddToSimulator?: () => void;
  onPreviewSlice?: () => void;
  previewActiveKey?: string | readonly string[];
  previewLoadingKey?: string | readonly string[];
  previewDisabled?: boolean;
  isSlicePicked?: boolean;
  managementStrip?: React.ReactNode;
};

function normalizeDefKey(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\(~\d+\s*bars\)/gi, "")
    .replace(/\(short dir\)|\(long window\)/gi, "")
    .replace(/\s+/g, "_")
    .replace(/[^\w|+]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function configuredSliceDefinitionTooltip(
  label: string,
  metricDefinitions?: Record<string, unknown>,
): string {
  const defs = metricDefinitions ?? {};
  const display = String(label ?? "").trim();
  if (!display) return "";
  const tooltipOptions = { labelText: display };
  const candleMetricFromLabel = candleDirectionMetricKeyFromLabelText(display);

  // Direct metric:variant / metric=value patterns (e.g. regime:expansion)
  if (/[:=]/.test(display)) {
    const [kRaw, ...vRest] = display.split(/[:=]/);
    const k = normalizeDefKey(kRaw);
    const v = normalizeDefKey(vRest.join(":"));
    if (k && v) {
      const pairText = marketContextVariantTooltipText(k, v, defs, tooltipOptions);
      if (pairText) return pairText;
    }
  }

  const directCandidates = [
    display,
    display.replace(/\s+\+\s+/g, "|"),
  ]
    .map(normalizeDefKey)
    .filter(Boolean);

  for (const key of directCandidates) {
    if (candleMetricFromLabel && (key === "up" || key === "down" || key === "flat")) {
      const cdText = marketContextVariantTooltipText(candleMetricFromLabel, key, defs, tooltipOptions);
      if (cdText) return cdText;
    }
    if (defs[key] != null) return ensureSentence(toNaturalMetricDefinition(defs[key]));
  }

  const parts = display
    .split(/\s*\+\s*|\s*\|\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return "";

  const definedParts = parts
    .map((part) => {
      const token = normalizeDefKey(part);
      if (!token) return "";
      if (candleMetricFromLabel && (token === "up" || token === "down" || token === "flat")) {
        return marketContextVariantTooltipText(candleMetricFromLabel, token, defs, tooltipOptions);
      }
      const inferredText =
        token === "long" || token === "short"
          ? marketContextVariantTooltipText("trade_direction", token, defs, tooltipOptions)
          : marketContextVariantTooltipText("regime", token, defs, tooltipOptions);
      if (inferredText && !inferredText.startsWith("This slice matches")) return inferredText;
      if (defs[token] != null) return ensureSentence(toNaturalMetricDefinition(defs[token]));
      return "";
    })
    .filter(Boolean);
  if (definedParts.length) return definedParts.join(" ");

  const single = normalizeDefKey(display);
  if (single && defs[single] != null) return ensureSentence(toNaturalMetricDefinition(defs[single]));
  return "";
}

function configuredSlicePreviewRowKey(rowId: string): string {
  return `configured-slice:${rowId}`;
}

function createConfiguredSliceResultColumns(
  props: MarketContextConfiguredSliceResultTableProps,
): ColumnDef<ConfiguredSliceResultRow>[] {
  const showPickColumn = Boolean(props.onAddToSimulator);
  const showPreviewColumn = Boolean(props.onPreviewSlice);

  const cols: ColumnDef<ConfiguredSliceResultRow>[] = [
    {
      id: "slice",
      accessorFn: (row) => row.sliceLabel,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Slice" align="left" />
      ),
      cell: ({ row }) => {
        const label = row.original.sliceLabel;
        const tooltipText = configuredSliceDefinitionTooltip(label, props.metricDefinitions);
        return (
          <TradeLogSortableCell
            data-market-context-wrap-cell=""
            className="min-w-[16rem] whitespace-normal break-words font-medium"
          >
            {tooltipText ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="cursor-help underline decoration-dotted underline-offset-2"
                      data-no-row-click=""
                    >
                      {label}
                    </span>
                  </TooltipTrigger>
                  <MarketContextTooltipContent widthClass="max-w-[360px]">
                    {tooltipText}
                  </MarketContextTooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              label
            )}
          </TradeLogSortableCell>
        );
      },
    },
  ];

  if (showPickColumn) {
    cols.push({
      id: "simulatorPick",
      enableSorting: false,
      header: () => <AddToSimulatorColumnHeader />,
      cell: () => (
        <MarketContextActionColumnCell>
          <div data-market-context-simulator-pick-cell="">
            <SimulatorPickActionCell
              picked={Boolean(props.isSlicePicked)}
              addAriaLabel="Add to Simulator"
              onAdd={() => props.onAddToSimulator?.()}
            />
          </div>
        </MarketContextActionColumnCell>
      ),
    });
  }

  cols.push(
    {
      id: "sample",
      accessorFn: (row) => row.sample ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Sample"
          tooltip="All trades in this row, including scratch/breakeven."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtInt(row.original.sample)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "winRate",
      accessorFn: (row) => row.winRate ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Win rate" tooltip={WIN_RATE_COLUMN_TOOLTIP} />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtPct(row.original.winRate)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "expectancy",
      accessorFn: (row) => row.expectancy ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Expectancy"
          tooltip="Average expected return per trade for the slice."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtNum(row.original.expectancy)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "totalPnl",
      accessorFn: (row) => row.totalPnl ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Total PnL"
          tooltip="Total profit and loss across all trades in the slice."
        />
      ),
      cell: ({ row }) => {
        const totalPnl = row.original.totalPnl;
        if (totalPnl == null || !Number.isFinite(totalPnl)) {
          return (
            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            <span data-trade-log-pnl="" style={{ color: pnlBarFill(totalPnl) }}>
              {fmtPnl(totalPnl)}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "bestSituation",
      accessorFn: (row) => row.bestSituation ?? "",
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Top best situation" align="left" />
      ),
      cell: ({ row }) => {
        const text = row.original.bestSituation ?? "—";
        return (
          <TradeLogSortableCell
            data-market-context-wrap-cell=""
            data-market-context-situation-cell=""
            className="min-w-0 max-w-full overflow-hidden text-sm"
            title={text === "—" ? undefined : text}
          >
            <span className="block max-w-full truncate">{text}</span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "worstSituation",
      accessorFn: (row) => row.worstSituation ?? "",
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Top worst situation" align="left" />
      ),
      cell: ({ row }) => {
        const text = row.original.worstSituation ?? "—";
        return (
          <TradeLogSortableCell
            data-market-context-wrap-cell=""
            data-market-context-situation-cell=""
            className="min-w-0 max-w-full overflow-hidden text-sm"
            title={text === "—" ? undefined : text}
          >
            <span className="block max-w-full truncate">{text}</span>
          </TradeLogSortableCell>
        );
      },
    },
  );

  if (showPreviewColumn) {
    cols.push({
      id: "previewTrades",
      enableSorting: false,
      header: () => <MarketContextPreviewTradesColumnHeader />,
      cell: ({ row }) => {
        const rowKey = configuredSlicePreviewRowKey(row.original.id);
        return (
          <MarketContextPreviewTradesButton
            rowKey={rowKey}
            activeKey={props.previewActiveKey}
            loadingKey={props.previewLoadingKey}
            disabled={Boolean(props.previewDisabled)}
            onClick={(e) => {
              e.stopPropagation();
              props.onPreviewSlice?.();
            }}
          />
        );
      },
    });
  }

  return cols;
}

export function MarketContextConfiguredSliceResultTable(
  props: MarketContextConfiguredSliceResultTableProps,
): React.ReactElement {
  const data = useMemo(() => [props.row], [props.row]);
  const columns = useMemo(
    () => createConfiguredSliceResultColumns(props),
    [
      props.isSlicePicked,
      props.metricDefinitions,
      props.onAddToSimulator,
      props.onPreviewSlice,
      props.previewActiveKey,
      props.previewDisabled,
      props.previewLoadingKey,
    ],
  );

  return (
    <div data-market-context-configured-slice-result="" className="mb-6 w-full min-w-0">
      <Field className="mb-4 w-full">
        <FieldLabel className={MARKET_CONTEXT_FIELD_LABEL_CLASS}>{props.title}</FieldLabel>
        <FieldDescription className={MARKET_CONTEXT_FIELD_DESC_CLASS}>{props.description}</FieldDescription>
      </Field>
      <div data-market-context-configured-slice-result-table="" className="w-full min-w-0 rounded-[6px]">
        <DataTable
          columns={columns}
          data={data}
          getRowId={(row) => row.id}
          showPagination={false}
          emptyMessage="No slice result."
          className="min-w-0"
        />
      </div>
      {props.managementStrip ?? null}
    </div>
  );
}
